import { useState } from 'react';
import { ImagePlus, Loader2 } from 'lucide-react';
import { api } from '../hooks/api';

interface Props {
  onSuccess: (url: string) => void;
  variables?: Record<string, string> | undefined;
  mode?: 'default' | 'button';
}

export function ImageUploader({ onSuccess, variables, mode = 'default' }: Props) {
  const [isUploading, setIsUploading] = useState(false);

  const handleImageUpload: React.ChangeEventHandler<HTMLInputElement> = async (event) => {
    const file = event.target.files?.[0];
    if (file) {
      setIsUploading(true);
      const asset = await api.assets.upload(file, variables);
      if (asset) {
        onSuccess(asset.url);
      }
      setIsUploading(false);
    }
  };

  const fileInput = (
    <input
      type="file"
      accept="image/*"
      className="hidden"
      onChange={handleImageUpload}
      disabled={isUploading}
    />
  );

  if (mode === 'button') {
    return (
      <label
        className={`border-line bg-bg/60 text-ink-soft hover:border-accent-border hover:text-accent inline-flex items-center justify-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[11.5px] font-semibold transition-colors ${
          isUploading ? 'pointer-events-none opacity-70' : 'cursor-pointer'
        }`}
      >
        {isUploading ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}
        {isUploading ? 'Uploading…' : 'Upload image'}
        {fileInput}
      </label>
    );
  }

  return (
    <label className="group border-line-strong/60 bg-bg-soft/50 hover:border-accent-border hover:bg-accent/5 flex cursor-pointer flex-col items-center justify-center gap-2.5 rounded-2xl border-2 border-dashed px-6 py-10 text-center transition-all duration-200">
      <span className="bg-accent/10 text-accent flex h-11 w-11 items-center justify-center rounded-full shadow-[inset_0_0_0_1px_var(--color-accent-border)] transition-transform duration-200 group-hover:scale-105">
        {isUploading ? <Loader2 size={18} className="animate-spin" /> : <ImagePlus size={18} />}
      </span>
      <span className="text-ink-soft group-hover:text-accent text-sm font-semibold transition-colors">
        {isUploading ? 'Uploading…' : 'Click to upload an image'}
      </span>
      <span className="text-muted text-xs">PNG, JPG, WebP or GIF — up to 20 MB</span>
      {fileInput}
    </label>
  );
}
